import { useState } from 'react';
import Modal from './Modal';
import ConfirmModal from './ConfirmModal';
import UserAvatar from './UserAvatar';
import CustomSelect from './CustomSelect';
import StoryPointsInput from './StoryPointsInput';
import FormField from './shared/FormField';
import { DeleteIcon, EditIcon, CheckIcon, CopyIcon } from './Icons';
import { TYPE_CONFIG, TYPE_OPTIONS, STATUS_OPTIONS, KANBAN_STATUS_OPTIONS, LOCATION_OPTIONS_VERBOSE, PRIORITY_OPTIONS, PRIORITY_CONFIG, CONTAINER_TYPES, RETRO_STATUS_OPTIONS } from '../constants';
import { findAssignee, buildUserOptions, getItemBreadcrumb } from '../utils';

export default function SprintItemModal({ item, onClose, onUpdate, onDelete, users, isKanban, isRetro, allItems, initialEditing = false }) {
  const [editing, setEditing] = useState(initialEditing);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description || '');
  const [type, setType] = useState(item.type);
  const [storyPoints, setStoryPoints] = useState(item.storyPoints ?? '');
  const [status, setStatus] = useState(item.status);
  const [assigneeId, setAssigneeId] = useState(item.assigneeId || '');
  const [location, setLocation] = useState(item.location || 'board');
  const [priority, setPriority] = useState(item.priority || 'medium');
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
  const Icon = config.icon;
  const assignee = findAssignee(users, item.assigneeId);
  const isContainer = CONTAINER_TYPES.includes(item.type);

  const userOptions = buildUserOptions(users);

  const breadcrumb = allItems ? getItemBreadcrumb(item.id, allItems) : [];

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    let data;
    if (isRetro) {
      data = { title: title.trim(), description: description.trim(), status };
    } else {
      data = {
        title: title.trim(),
        description: description.trim(),
        type,
        storyPoints: storyPoints === '' ? null : Number(storyPoints),
        status,
        assigneeId: assigneeId || null,
        priority: isKanban ? priority : null,
      };
      if (!isContainer) {
        data.location = location;
      }
    }
    await onUpdate(item.id, data);
    setSaving(false);
    onClose();
  };

  const handleDelete = async () => {
    await onDelete(item.id);
    onClose();
  };

  const footer = (
    <div className="modal-footer-actions">
      <button className="btn btn-danger btn-sm" onClick={() => setShowConfirm(true)}>
        <DeleteIcon size={14} /> Delete
      </button>
      {editing ? (
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          <CheckIcon size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>
      ) : (
        <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
          <EditIcon size={14} /> Edit
        </button>
      )}
    </div>
  );

  const modalTitle = isRetro ? 'Retro Note' : 'Sprint Item';

  return (
    <>
      <Modal title={modalTitle} onClose={onClose} footer={footer}>
        {breadcrumb.length > 0 && (
          <div className="sprint-item-breadcrumb">
            {breadcrumb.map((parent, i) => {
              const pConfig = TYPE_CONFIG[parent.type] || TYPE_CONFIG.task;
              return (
                <span key={parent.id}>
                  <span style={{ color: pConfig.color }}>{pConfig.label}</span>
                  {': '}
                  {parent.title}
                  {i < breadcrumb.length - 1 && <span className="breadcrumb-sep"> / </span>}
                </span>
              );
            })}
          </div>
        )}
        {isRetro ? (
          editing ? (
            <div className="sprint-item-edit-form">
              <FormField label="Column">
                <CustomSelect value={status} onChange={setStatus} options={RETRO_STATUS_OPTIONS} />
              </FormField>
              <FormField label="Title">
                <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
              </FormField>
              <FormField label="Note">
                <textarea className="form-input retro-note-textarea" rows={7} value={description} onChange={(e) => setDescription(e.target.value)} />
              </FormField>
            </div>
          ) : (
            <div className="sprint-item-detail">
              <div className="sprint-item-detail-meta">
                <span className={`status-badge retro-badge retro-badge--${item.status}`}>
                  {RETRO_STATUS_OPTIONS.find((o) => o.value === item.status)?.label}
                </span>
              </div>
              <h3 className="sprint-item-detail-title">{item.title}</h3>
              {item.description && (
                <p className="sprint-item-detail-desc">{item.description}</p>
              )}
            </div>
          )
        ) : (
          editing ? (
            <div className="sprint-item-edit-form">
              <FormField label="Type">
                <CustomSelect value={type} onChange={setType} options={TYPE_OPTIONS} />
              </FormField>
              <FormField label="Title">
                <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} />
              </FormField>
              <FormField label="Description">
                <textarea className="form-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
              </FormField>
              <div className="form-row">
                <FormField label="Story Points">
                  <StoryPointsInput value={storyPoints} onChange={setStoryPoints} />
                </FormField>
                <FormField label="Status">
                  <CustomSelect value={status} onChange={setStatus} options={isKanban ? KANBAN_STATUS_OPTIONS : STATUS_OPTIONS} />
                </FormField>
              </div>
              <FormField label="Assign To">
                <CustomSelect value={assigneeId} onChange={setAssigneeId} options={userOptions} placeholder="Unassigned" />
              </FormField>
              {!isContainer && (
                <FormField label="Location">
                  <CustomSelect value={location} onChange={setLocation} options={LOCATION_OPTIONS_VERBOSE} />
                </FormField>
              )}
              {isKanban && (
                <FormField label="Priority">
                  <CustomSelect value={priority} onChange={setPriority} options={PRIORITY_OPTIONS} />
                </FormField>
              )}
            </div>
          ) : (
            <div className="sprint-item-detail">
              <div className="sprint-item-detail-type" style={{ color: config.color }}>
                <Icon size={18} /> {config.label}
                {item.readableId && (
                  <span
                    className="sprint-item-rid sprint-item-rid--lg"
                    title="Copy ID"
                    onClick={() => navigator.clipboard.writeText(`${item.readableId}-${item.title}`)}
                  >
                    {item.readableId} <CopyIcon size={12} />
                  </span>
                )}
              </div>
              <h3 className="sprint-item-detail-title">{item.title}</h3>
              {item.description && (
                <p className="sprint-item-detail-desc">{item.description}</p>
              )}
              <div className="sprint-item-detail-meta">
                <span className={`status-badge status-${item.status}`}>
                  {(isKanban ? KANBAN_STATUS_OPTIONS : STATUS_OPTIONS).find((o) => o.value === item.status)?.label}
                </span>
                {isKanban && item.priority && (
                  <span className="priority-badge priority-badge--lg" style={{ background: PRIORITY_CONFIG[item.priority]?.color }}>
                    {PRIORITY_CONFIG[item.priority]?.label}
                  </span>
                )}
                {item.storyPoints != null && (
                  <span className="sprint-item-points-lg">{item.storyPoints} pts</span>
                )}
              </div>
              {assignee && (
                <div className="sprint-item-detail-assignee">
                  <UserAvatar user={assignee} size={32} showStatus />
                  <span className="sprint-item-detail-assignee-name">{assignee.displayName}</span>
                </div>
              )}
              {item.location === 'backlog' && (
                <div className="sprint-item-detail-meta" style={{ marginTop: '8px' }}>
                  <span className="status-badge" style={{ background: 'rgba(156,163,175,0.15)', color: '#9ca3af' }}>Backlog</span>
                </div>
              )}
            </div>
          )
        )}
      </Modal>

      {showConfirm && (
        <ConfirmModal
          message={isContainer
            ? "Delete this item and all its children? This action cannot be undone."
            : "Are you sure you want to delete this item? This action cannot be undone."}
          onConfirm={handleDelete}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  );
}
